import { Box } from "@mui/material";
import * as React from "react";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { FormFieldMargin } from "./TaskDynamicForm.styles";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";

export interface ITaskDynamicFormSignatureProp {
    isReadOnly: boolean;
    colIsReadOnly: boolean;
    taskTypeCol: ITaskTypeCol;
    taskDataRowCol: ITaskDataRowCol;
    onChange: (dataCol: ITaskDataRowCol) => void;
}

export const TaskDynamicFormSignature = (props: ITaskDynamicFormSignatureProp) => {
    const container = useRef<HTMLDivElement>();
    const canvas = useRef<SignatureCanvas>();
    const [size, setSize] = useState<DOMRect>();

    const typeCol = props.taskTypeCol;
    const dataCol = props.taskDataRowCol;
    
    const resizeContainer = () => {
        const containerRect = container.current.getBoundingClientRect();
        setSize(containerRect);
    };

    const resizeCanvas = () => {
        if (!container.current) return;
        const containerRect = container.current.getBoundingClientRect();
        setSize(containerRect);
        if (!canvas.current) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const aCanvas = canvas.current.getCanvas();
        aCanvas.width = containerRect.width * ratio;
        aCanvas.height = containerRect.height * ratio;
        aCanvas.getContext("2d").scale(ratio, ratio);
        canvas.current.clear(); // otherwise isEmpty() might return incorrect value
        if (!dataCol?.valueStr) return;
        canvas.current.fromDataURL(`data:image/png;base64,${dataCol?.valueStr}`);
    };

    useEffect(() => {
        window.addEventListener("resize", resizeCanvas);
        return () => window.removeEventListener("resize", resizeCanvas);
    }, []);

    useEffect(() => {
        if (!container.current) return;
        resizeContainer();
    }, [container.current, props.taskTypeCol?.class]);

    useEffect(() => {
        if (!container.current || !canvas.current) return;
        resizeCanvas();
    }, [container.current, canvas.current]);
    
    useEffect(() => {
        if (!canvas.current) return;
        if (!dataCol?.valueStr) return;
        canvas.current.fromDataURL(`data:image/png;base64,${dataCol?.valueStr}`);
    }, [props.taskDataRowCol?.valueStr, canvas.current]);
    
    const handleChange = (e: any) => {
        if (!canvas.current) return;
        const newValue = new TaskDataRowCol(dataCol);
        const image = canvas.current.toDataURL(); //PNG
        if (!image) return;
        const index = image.indexOf(",");
        if (index < 0) return;
        newValue.valueStr = image.substring(index + 1);
        props.onChange(newValue);
    };

    const handleClear = () => {
        if (!canvas.current) return;
        canvas.current.clear();
        const newValue = new TaskDataRowCol(dataCol);
        newValue.valueStr = "";
        props.onChange(newValue);
    };

    const isReadOnly = props.isReadOnly || props.colIsReadOnly;

    const clearButton = isReadOnly
        ? undefined
        : <Box className="signatureClear" sx={{ marginLeft: "auto", marginBottom: "-10px" }}><RemoveToolButton onClick={handleClear} /></Box>;

    return (
        <FormField
            title={typeCol.title}
            required={typeCol.required && !isReadOnly}
            margin={FormFieldMargin}
            buttons={clearButton}
        >
            {!typeCol.title && !isReadOnly &&
                <Box display="flex" sx={{ marginBottom: "10px" }}>
                    {clearButton}
                </Box>
            }
            <Box className="signature" ref={container} sx={{ pointerEvents: isReadOnly ? "none" : undefined, cursor: isReadOnly ? "not-allowed" : undefined }}>
                {(isReadOnly || !size
                    ? <Box
                        component="img"
                        sx={{
                            height: "100%",
                            objectFit: "contain"
                        }}
                        src={dataCol?.valueStr ? `data:image/png;base64,${dataCol?.valueStr}` : ""}
                    />
                    : <SignatureCanvas
                        ref={canvas}
                        canvasProps={{ width: size.width + "px", height: size.height + "px", style: { border: "1px solid rgba(0, 0, 0, 0.23)", borderRadius: "4px", width: size.width + "px", height: size.height + "px" } }}
                        dotSize={2}
                        onEnd={(e) => {
                            handleChange(e);
                        }}
                    />
                )}
            </Box>
        </FormField>
    );
};